import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CoreModule } from '../../../../../core/core.module';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator
} from '@angular/forms';
import { Subject } from 'rxjs';
import { IndicatorState } from '../../../../../shared/components/sb-pending-button-indicator/sb-pending-button-indicator.component';
import { takeUntil, tap } from 'rxjs/operators';
import { filterNilValue } from '@datorama/akita';
import { TestSelectorDirective } from '../../../../../shared/directives/test-selector.directive';
import { MatFormFieldAppearance } from '@angular/material/form-field';

@Component({
    selector: 'sb-generic-type-select',
    standalone: true,
    imports: [
        CommonModule,
        CoreModule,
        MatFormFieldModule,
        MatInputModule,
        MatOptionModule,
        MatSelectModule,
        TestSelectorDirective
    ],
    templateUrl: './sb-generic-type-select.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: SbGenericTypeSelectComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: SbGenericTypeSelectComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [
        `
            :host[appearance='none'] ::ng-deep {
                .mat-form-field-wrapper {
                    transition: all 300ms ease-in-out;
                    font-size: var(--sb-font-size, inherit);
                    mat-select {
                        font-weight: var(--sb-font-weight-bold, 700);
                    }
                    .mat-form-field-infix {
                        width: fit-content;
                    }
                }
                .mat-form-field-wrapper .mat-select .mat-select-trigger {
                    width: auto;
                }
            }
            // remove border and padding from mat-form-field when added to a table with class .table
            ::ng-deep .table sb-generic-type-select[appearance='none'] ::ng-deep {
                .mat-form-field-infix {
                    border-width: 0 !important;
                    padding: 0 !important;
                }

                .mat-form-field-wrapper {
                    padding: 0 !important;
                }
            }
        `
    ]
})
export class SbGenericTypeSelectComponent
    implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
    get disabled(): boolean {
        return this._disabled;
    }

    @Input() set disabled(value: boolean) {
        this._disabled = value;
        if (this.disabled) {
            this.selectCtrl.disable();
        } else {
            this.selectCtrl.enable();
        }
    }
    get label(): string {
        return this._label;
    }

    @Input() set label(value: string) {
        this._label = value;
    }
    get options(): Array<{ value: any; label: string }> {
        return this._options;
    }

    @Input() set options(value: Array<{ value: any; label: string }>) {
        this._options = value;
    }
    @Input() set touched(value: boolean) {
        if (value) {
            this.markAsTouched();
            this.selectCtrl.markAsTouched();
        } else {
            this._touched = value;
            this.selectCtrl.markAsUntouched();
        }
    }

    get sbTestSelector(): string {
        return this._sbTestSelector;
    }
    @Input() set sbTestSelector(value: string) {
        this._sbTestSelector = value;
    }

    @Input() valueKey: string | false = 'value';

    constructor(private _cdr: ChangeDetectorRef) {}

    private _label: string;

    unsubscribe$ = new Subject();
    private _options: Array<{ value: any; label: string }> = [];
    selectCtrl = new FormControl<(typeof this._options)[number]>(null);
    state: IndicatorState = null;
    selected: (typeof this._options)[number];
    private _sbTestSelector: string = 'instrument-identifier-type-select';

    onChange = (value) => {};

    onTouched = () => {};

    private _touched = false;

    private _disabled;

    writeValue(value) {
        this.selectCtrl.patchValue(value);
    }

    registerOnChange(onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any) {
        this.onTouched = onTouched;
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this._touched = true;
        }
    }

    /*setDisabledState(disabled: boolean) {
        this._disabled = disabled;
        console.log('disabled', disabled, this._label);
        if (this.disabled) {
            this.selectCtrl.disable();
        } else {
            this.selectCtrl.enable();
        }
    }*/
    @Input() appearance: MatFormFieldAppearance | 'none' = 'fill';

    compareFn = (o1: (typeof this._options)[0], o2: (typeof this._options)[0]) => {
        if (this.valueKey === false) {
            return o1.value === o2.value;
        } else {
            return o1 === o2;
        }
    };

    validate(control: AbstractControl): ValidationErrors | null {
        // inherit errors from control value accessor
        setTimeout(() => {
            this.selectCtrl.setErrors(control.errors);
        });
        // no custom validators implemented
        return null;
    }

    ngOnInit(): void {
        this.selectCtrl.valueChanges
            .pipe(
                tap((value) => {
                    this.markAsTouched();
                    this.onChange(value);
                }),
                filterNilValue(),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((selectedValue) => (this.selected = selectedValue));
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
